import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import { ComponentProps, useCallback, useLayoutEffect, useRef, useState, VFC } from "react";
import { useFragment } from "react-relay";
import { Col, Row } from "reactstrap";

import { IPaymentFormChildProps } from "components/payment/PaymentForm";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { StandardPaymentButtonLayout } from "components/payment/StandardPaymentButtonLayout";
import { getMonerisTokenizationResponse } from "components/payment/moneris/getMonerisTokenizationResponse";
import styles from "components/payment/moneris/MonerisProvider.module.scss";
import { MonerisResponseCode } from "components/payment/moneris/MonerisResponseCode";
import { LoadingMessage } from "components/shared/LoadingMessage";
import { paymentMessages } from "domain/payment/paymentMessages";
import { MonerisProvider_configuration$key } from "generatedQueries/MonerisProvider_configuration.graphql";
import { mapMessages } from "shared/mapMessages";
import { BasePaymentButton } from "components/payment/BasePaymentButton";
import { useIntl } from "react-intl";

const messages = mapMessages("components.payment.moneris.MonerisProvider", {
    failedToParseResponse: "Failed to parse response from Moneris. Please try refreshing the page and if the problem persists, please contact {organisationContact}.",
    invalidConfiguration: "Moneris payment provider has not been configured correctly. Please try refreshing the page and if the problem persists, please contact {organisationContact}.",
    invalidCardNumber: "There was a problem with the Card Number. Please check the card details and try again.",
    invalidCardCvd: "There was a problem with the CVD. Please check the card details and try again.",
    invalidCardExpiryDate: "There was a problem with the Expiry Date. Please check the card details and try again.",
    unknownMonerisError: "Moneris payment provider has returned an unknown error. Please try refreshing the page and if the problem persists, please contact {organisationContact}.",
});

type MonerisResponseState = {
    errorMessages: (Pick<ComponentProps<typeof ErrorMessage>, "message" | "messageValues"> & { key: string })[];
    isProcessing: boolean;
};

type Props = Pick<IPaymentFormChildProps, "amountToCharge" | "errorMessages" | "isPaymentProcessing" | "submitPayment"> & {
    allPaymentRowsAreValid: boolean;
    configuration: MonerisProvider_configuration$key;
    termsAndConditionsAccepted: boolean;
}

export const MonerisProvider: VFC<Props> = ({
    allPaymentRowsAreValid,
    amountToCharge,
    configuration,
    errorMessages,
    isPaymentProcessing,
    submitPayment,
    termsAndConditionsAccepted,
}) => {
    const { formatMessage } = useIntl();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [monerisResponse, setMonerisResponse] = useState<MonerisResponseState>({ errorMessages: [], isProcessing: false });

    const { currency, organisationContact, monerisHostedTokenization } = useFragment<MonerisProvider_configuration$key>(fragment, configuration);

    const onMessage = useCallback((event: WindowEventMap["message"]) => {
        if (!monerisHostedTokenization?.uri || event.origin !== new URL(monerisHostedTokenization.uri).origin) {
            return;
        }

        const tokenizationResponse = getMonerisTokenizationResponse(event.data);
        if (!tokenizationResponse) {
            setMonerisResponse({
                errorMessages: [{ key: "0", message: messages.failedToParseResponse, messageValues: { organisationContact } }],
                isProcessing: false,
            });
            return;
        }

        if (tokenizationResponse.responseCode.length &&
            tokenizationResponse.responseCode.every(c => c === MonerisResponseCode.Success) &&
            tokenizationResponse.dataKey) {
            setMonerisResponse({
                errorMessages: [],
                isProcessing: false,
            });
            submitPayment({
                monerisPaymentRequest: { token: tokenizationResponse.dataKey },
            });
            return;
        }

        setMonerisResponse({
            errorMessages: tokenizationResponse.responseCode.map((responseCode, index) => {
                let message: MonerisResponseState["errorMessages"][0]["message"];
                let messageValues: MonerisResponseState["errorMessages"][0]["messageValues"] = undefined;

                switch(responseCode) {
                    case MonerisResponseCode.ErrorGeneratingToken:
                        message = messages.unknownMonerisError;
                        messageValues = { organisationContact };
                        break;
                    case MonerisResponseCode.InvalidProfileId:
                    case MonerisResponseCode.InvalidProfileIdOrSourceUrl:
                        message = messages.invalidConfiguration;
                        messageValues = { organisationContact };
                        break;
                    case MonerisResponseCode.InvalidCardData:
                        message = messages.invalidCardNumber;
                        break;
                    case MonerisResponseCode.InvalidCvd:
                        message = messages.invalidCardCvd;
                        break;
                    case MonerisResponseCode.InvalidExpiryDate:
                        message = messages.invalidCardExpiryDate;
                        break;
                    default:
                        message = paymentMessages.validationWarningMessage;
                        break;
                };

                return { key: `${responseCode}-${index}`, message, messageValues };
            }),
            isProcessing: false,
        });
    }, [monerisHostedTokenization, submitPayment, organisationContact]);

    useLayoutEffect(() => {
        window.addEventListener("message", onMessage);

        return () => {
            window.removeEventListener("message", onMessage);
        };
    }, [onMessage]);

    useLayoutEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) {
            return;
        }

        iframe.addEventListener("load", onIframeLoad);

        if (iframe.contentDocument?.readyState === "complete") {
            onIframeLoad();
        }

        return () => {
            iframe.removeEventListener("load", onIframeLoad);
        };

        function onIframeLoad() {
            setIsIframeLoading(false);
        }
    }, []);

    if (!monerisHostedTokenization?.profileId || !monerisHostedTokenization?.uri) {
        return <ErrorMessage message={messages.invalidConfiguration} messageValues={{ organisationContact }} />;
    }

    const isPaymentValid = termsAndConditionsAccepted && allPaymentRowsAreValid && amountToCharge > 0;
    const isProcessingTotal = isPaymentProcessing || monerisResponse.isProcessing;
    const isDisabled = !isPaymentValid || isProcessingTotal || isIframeLoading;

    const disabledReason = isDisabled && !isProcessingTotal && !isIframeLoading
        ? formatMessage(paymentMessages.paymentButtonDisabledWarning)
        : undefined;

    return <Row>
        <Col>
            {isIframeLoading && <LoadingMessage />}
            <iframe
                className={classNames({
                    [styles.iframe]: true,
                    "d-none": isIframeLoading,
                })}
                id="MonerisIframe"
                ref={iframeRef}
                src={buildIframeUrl(monerisHostedTokenization.uri, monerisHostedTokenization.profileId)}
                title={formatMessage(paymentMessages.payHeaderLabel)} />
            <StandardPaymentButtonLayout
                payButton={
                    <BasePaymentButton
                        amount={amountToCharge}
                        currency={currency}
                        isDisabled={isDisabled}
                        isProcessing={isProcessingTotal}
                        onClick={onSubmit}
                        className={classNames({ "w-50": true, "d-none": isIframeLoading })}
                        data-testid="payButton"
                        disabledReason={disabledReason}
                    />
                }
                errorMessages={<>
                    {errorMessages}
                    {monerisResponse.errorMessages.map((error) => (
                        <div className="mt-3" key={error.key} role="alert">
                            <ErrorMessage message={error.message} messageValues={error.messageValues} />
                        </div>
                    ))}
                </>} />
        </Col>
    </Row>;

    function onSubmit() {
        if (!iframeRef.current?.contentWindow || !monerisHostedTokenization?.uri) {
            return;
        }
        setMonerisResponse({ errorMessages: [], isProcessing: true });
        iframeRef.current.contentWindow.postMessage("tokenize", monerisHostedTokenization.uri);
    }
};

function buildIframeUrl(uri: string, profileId: string): string {
    const monerisIframeUrl = new URL(uri);
    monerisIframeUrl.search = new URLSearchParams({
        id: profileId,
        pmmsg: "true",
        display_labels: "1",
        enable_exp: "1",
        enable_exp_formatting: "1",
        enable_cvd: "1",
        enable_cc_formatting: "1",
        css_body: minimizeCss(`
            max-width: 400px;
            height: 350px;
            font-size: 16px;
            font-family: sans-serif;
            font-weight: 400;
            line-height: 1.5;
            color: #212529;
        `),
        css_input_label: minimizeCss(`
            display: block;
            width: 100%;
            font-size: 1rem;
            margin-bottom: 0.5rem;
        `),
        css_textbox: minimizeCss(`
            width: 100%;
            height: 2.5rem;
            padding: 0.375rem 0.75rem;
            font-size: 1rem;
            color: #495057;
            background-color: #fff;
            border: 1px solid #ced4da;
            border-radius: 0.25rem;
            box-sizing: border-box;
            margin-bottom: 1rem;
        `),
        css_label_pan: minimizeCss(`
            width: 100%;
        `),
        css_textbox_pan: minimizeCss(`
            width: 100%
        `),
        css_label_exp: minimizeCss(`
            width: 65%;
        `),
        css_textbox_exp: minimizeCss(`
            width: 65%;
        `),
        css_label_cvd: minimizeCss(`
            width: 65%;
        `),
        css_textbox_cvd: minimizeCss(`
            width: 65%;
        `),
    }).toString();

    return monerisIframeUrl.toString();

    function minimizeCss(input: string): string {
        return input.replace(/[\t\r\n]/gm, "").replace(/ {2,}/gm, " ");
    }
}

const fragment = graphql`
    fragment MonerisProvider_configuration on Configuration {
        currency
        organisationContact
        monerisHostedTokenization {
            uri
            profileId
        }
    }
`;
